import React, { PureComponent } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';

import { Loader } from 'shared/components';

import MediaView from './MediaView';
import MediaViewControl from './MediaViewControl';
import theme from './MediaViewer.scss';
import mediaType from './mediaType';

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 2;
const ZOOM_RATIO = 0.2;

const precision = (val) => Math.round(val * 100) / 100;

// TODO: Add mousewheel support to zoom

class MediaViewer extends PureComponent {
  static propTypes = {
    fetchMediaData: PropTypes.func.isRequired,
    allowedActions: PropTypes.arrayOf(PropTypes.string).isRequired,
    views: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        thumb: PropTypes.string,
        src: PropTypes.string,
        downloadRoute: PropTypes.string,
        name: PropTypes.string,
        comment: PropTypes.string,
        delete: PropTypes.func,
      }).isRequired,
    ).isRequired,
    showViewId: PropTypes.number.isRequired,
    onClose: PropTypes.func,
    onDelete: PropTypes.func,
    isExpanded: PropTypes.bool,
    parentId: PropTypes.string,
  };

  static defaultProps = {
    onClose: noop,
    onDelete: noop,
    isExpanded: false,
    parentId: '',
  };

  constructor(props) {
    super(props);

    this.mediaParentContainer = document.createElement('div');
    this.mediaRootContainer = document.createElement('div');

    this.state = {
      loading: true,
      isExpanded: props.isExpanded,
      showViewId: props.showViewId,
      src: '',
      comment: '',
      zoom: 1,
      rotation: 0,
      imageWasDragged: false,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyboard, true);

    this.parentContainer = document.getElementById(this.props.parentId);
    this.rootContainer = document.getElementById('rootPage');

    if (this.props.parentId && this.parentContainer) {
      this.parentContainer.appendChild(this.mediaParentContainer);
    }

    if (this.rootContainer) {
      this.rootContainer.appendChild(this.mediaRootContainer);
    }
    if (this.props.views.length) {
      this.loadView(this.getViewById(this.props.showViewId));
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyboard, true);
    this.parentContainer.removeChild(this.mediaParentContainer);
    this.rootContainer.removeChild(this.mediaRootContainer);
    this.close();
  }

  onImageDrag = () => {
    this.setState({
      imageWasDragged: true,
    });
  };

  loadView(i) {
    const { fetchMediaData } = this.props;
    const { src, name, comment, downloadRoute } = this.props.views[i];
    if (src) {
      return this.setState({
        src,
        comment,
        zoom: 1,
        rotation: 0,
      });
    }

    URL.revokeObjectURL(this.state.src);

    this.setState({
      zoom: 1,
      rotation: 0,
      loading: true,
    });

    return fetchMediaData(downloadRoute)
      .then((res) => res.blob())
      .then((blob) => {
        const image = new Image();
        const source = URL.createObjectURL(blob);

        image.src = source;
        image.onload = () => {
          this.setState({
            src: source,
            loading: false,
            comment,
            name,
          });
        };
      })
      .catch(noop);
  }

  toggleExpand() {
    this.setState({
      isExpanded: !this.state.isExpanded,
    });
  }

  getViewById = id => this.props.views.findIndex((view) => view.id === id);

  showView(num) {
    const { views } = this.props;
    const { showViewId } = this.state;
    const showViewIndex = this.getViewById(showViewId);

    const i = showViewIndex + num;

    if (i < 0 || i >= views.length) {
      return;
    }

    const item = views[i];

    if (item.mimeType === mediaType.PDF) {
      return this.showView(num + 1);
    }

    this.setState({
      showViewId: item.id,
    });

    return this.loadView(i);
  }

  zoom(ratio) {
    const zoom = precision(this.state.zoom + ratio);

    if (zoom < ZOOM_MIN || zoom > ZOOM_MAX) {
      return;
    }

    return this.setState({
      zoom,
    });
  }

  rotate(ratio) {
    const rotation = (this.state.rotation + ratio) % 360;

    return this.setState({
      rotation,
    });
  }

  reset() {
    return this.setState({
      zoom: 1,
      rotation: 0,
      imageWasDragged: false,
    });
  }

  close() {
    URL.revokeObjectURL(this.state.src);
    this.props.onClose();
  }

  delete() {
    const { onDelete } = this.props;
    const { showViewId, comment } = this.state;

    return Promise
      .resolve(onDelete({
        id: showViewId,
        comment,
      }))
      .then(() => this.close())
      .catch(noop);
  }

  getActions = [
    {
      name: 'CLOSE',
      icon: 'naked-x',
      title: {
        id: 'const.mediaViewer.mediaViewControl.close',
        defaultMessage: '** Close',
      },
      ariaLabel: {
        id: 'UploadedIMageView.CloseButton.TitleAttribute',
        defaultMessage: '** Close image view',
      },
      onClick: () => this.close(),
    },
    {
      name: 'DELETE',
      icon: 'trash',
      title: {
        id: 'const.mediaViewer.mediaViewControl.delete',
        defaultMessage: '** Delete picture',
      },
      onClick: () => this.delete(),
    },

    {
      name: 'ZOOM_IN',
      icon: 'plus',
      title: {
        id: 'const.mediaViewer.mediaViewControl.zoomIn',
        defaultMessage: '** Zoom in',
      },
      onClick: () => this.zoom(ZOOM_RATIO),
    },

    {
      name: 'ZOOM_OUT',
      icon: 'minus',
      title: {
        id: 'const.mediaViewer.mediaViewControl.zoomOut',
        defaultMessage: '** Zoom out',
      },
      onClick: () => this.zoom(-ZOOM_RATIO),
    },

    {
      name: 'PREV',
      icon: 'chevron-left',
      title: {
        id: 'const.mediaViewer.mediaViewControl.prevImage',
        defaultMessage: '** Previous image',
      },
      onClick: () => this.showView(-1),
    },

    {
      name: 'NEXT',
      icon: 'chevron-right',
      title: {
        id: 'const.mediaViewer.mediaViewControl.nextImage',
        defaultMessage: '** Next image',
      },
      onClick: () => this.showView(1),
    },

    {
      name: 'ROTATE_LEFT',
      icon: 'rotate-left',
      title: {
        id: 'const.mediaViewer.mediaViewControl.rotateLeft',
        defaultMessage: '** Rotate left',
      },
      onClick: () => this.rotate(-90),
    },

    {
      name: 'ROTATE_RIGHT',
      icon: 'rotate-right',
      title: {
        id: 'const.mediaViewer.mediaViewControl.rotateRight',
        defaultMessage: '** Rotate right',
      },
      onClick: () => this.rotate(90),
    },

    {
      name: 'RESET',
      icon: 'reset',
      title: {
        id: 'const.mediaViewer.mediaViewControl.reset',
        defaultMessage: '** Reset',
      },
      onClick: () => this.reset(),
    },

    {
      name: 'TOGGLE_EXPAND',
      icon: 'expand',
      title: {
        id: 'const.mediaViewer.mediaViewControl.toggleExpand',
        defaultMessage: '** Toggle expand',
      },
      onClick: () => this.toggleExpand(),
    },
  ];

  runAction(name) {
    const action = this.getActions.find(a => a.name === name);
    if (action) {
      action.onClick();
    }
  }

  handleKeyboard = e => {
    const keyCode = e.keyCode || e.which || e.charCode;
    switch (keyCode) {
      // ESCAPE
      case 27:
        e.preventDefault();
        return this.runAction('CLOSE');

      // LEFT ARROW
      case 37:
        e.preventDefault();
        return this.runAction('PREV');

      // RIGHT ARROW
      case 39:
        e.preventDefault();
        return this.runAction('NEXT');

      // UP ARROW
      case 38:
        e.preventDefault();
        return this.runAction('ZOOM_IN');

      // DOWN ARROW
      case 40:
        e.preventDefault();
        return this.runAction('ZOOM_OUT');

      default:

    }
  };

  render() {
    const { allowedActions } = this.props;
    const {
      src,
      name,
      comment,
      zoom,
      rotation,
      loading,
      isExpanded,
      imageWasDragged,
    } = this.state;

    return ReactDOM.createPortal(
      <div
        className={ classnames(theme.root, {
          [theme.fullscreen]: isExpanded,
        }) }
      >
        { loading && (
          <div className={ theme.loader }>
            <Loader />
          </div>
        ) }

        { !loading && (<MediaView
          src={ src }
          name={ name }
          comment={ comment }
          zoom={ zoom }
          rotation={ rotation }
          onImageDrag={ this.onImageDrag }
          imageWasDragged={ imageWasDragged }
          isExpanded={ isExpanded }
        />) }

        <MediaViewControl
          isExpanded={ isExpanded }
          actions={ this.getActions }
          allowedActions={ allowedActions }
        />
      </div>,
      isExpanded ? this.mediaRootContainer : this.mediaParentContainer,
    );
  }
}

export default MediaViewer;
